import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Calendar, Clock, MapPin, ChevronLeft, ChevronRight,
  Activity, BarChart3, Users, AlertCircle, CheckCircle,
  Plus, Filter, Zap, Building2, Gauge, Heart,
  ClipboardList, Plane, Shield, Eye
} from 'lucide-react';
import PageLayout from '../../components/layout/PageLayout';
import { VUKZ } from '../../theme/vukzTheme';
import { AZURE } from '../../services/api';
import {
  getAgendaEvents,
  getCurrentWeekRange,
  getCurrentMonthRange,
  groupEventsByDate,
} from '../../services/agendaService';
import {
  parseAgendaQuery,
  getOriginLabel,
  getOriginRoute,
  buildFilterSummary,
} from '../../utils/agendaNavigation';
import ManagerAgendaPage from './ManagerAgendaPage';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import { useRBAC } from '../../contexts/RBACContext';
import { useAuth } from '../../contexts/AuthContext';

// Internal context so sub-components can access t without prop-drilling
const AgendaLangContext = React.createContext({ lang: 'pt', t: null });
const useAT = () => React.useContext(AgendaLangContext);

const AGENDA_LABELS = {
  pt: {
    views: [{ key: 'day', label: 'Dia' }, { key: 'week', label: 'Semana' }, { key: 'month', label: 'Mês' }],
    roleTabs: [
      { key: 'calendar', label: 'Calendário', desc: 'Eventos e treinos' },
      { key: 'athlete',  label: 'Atleta',     desc: 'Readiness e ACWR pessoal' },
      { key: 'coach',    label: 'Treinador',  desc: 'Elenco e sessões' },
      { key: 'prep',     label: 'Preparador', desc: 'Carga e monitoramento' },
      { key: 'manager',  label: 'Gestor',     desc: 'KPIs e visão macro' },
      { key: 'period',   label: 'Periodização', desc: 'Blocos e planejamento' },
    ],
    eventFilters: [{ id: 'all', label: 'Todos' }, { id: 'competition', label: 'Competições' }, { id: 'training', label: 'Treinos' }],
    zoneLabels: { optimal: 'Ótimo', good: 'Bom', undertraining: 'Subtreinamento', moderate: 'Moderado', caution: 'Atenção', low: 'Baixo', danger: 'Perigo', rest: 'Descanso', no_data: 'Sem dados' },
    availLabels: { available: 'Disponível', partial: 'Parcial', modified: 'Modificado', unavailable: 'Indisponível', injured: 'Lesionado', illness: 'Doente', personal: 'Pessoal' },
    complianceLabels: { valid: 'Válido', expiring_soon: 'Vencendo', expired: 'Vencido', pending: 'Pendente', scheduled: 'Agendado' },
    today: 'Hoje', markDone: 'Marcar Concluído',
    aiAlerts: (n) => `Alertas da IA (${n})`,
    sidebarLoad: 'Carga (7 dias)', sidebarSummary: 'Resumo',
    sidebarLoadEmpty: 'Dados disponíveis após completar treinos',
    totalEvents: 'Total eventos', completed: 'Concluídos', pending: 'Pendentes',
    noEvents: 'Nenhum evento neste período',
    // Athlete tab
    morningCheckin: 'Registrar Check-in Matinal',
    checkinTitle: 'Check-in Matinal', sleepHours: 'Horas de sono',
    checkinSave: 'Registrar Readiness', saving: 'Salvando…',
    sliders: [
      { key: 'perceived_fatigue',    label: 'Fadiga',       low: 'Fresco',  high: 'Exausto' },
      { key: 'perceived_soreness',   label: 'Dor Muscular', low: 'Nenhuma', high: 'Intensa' },
      { key: 'perceived_mood',       label: 'Humor',        low: 'Ruim',    high: 'Ótimo' },
      { key: 'perceived_stress',     label: 'Estresse',     low: 'Nenhum',  high: 'Alto' },
      { key: 'perceived_motivation', label: 'Motivação',   low: 'Baixa',   high: 'Alta' },
    ],
    readinessTitle: 'Readiness', acwrTitle: 'ACWR',
    readinessNoCheckin: 'Complete o check-in matinal para ver seu readiness',
    acwrAcute: 'TSS 7d (Agudo)', acwrChronic: 'TSS 28d (Crônico)',
    upcomingEvents: 'Próximos Eventos', noEventsSched: 'Nenhum evento agendado',
    // Coach tab
    squadToday: 'Elenco Hoje', dayResume: 'Resumo do Dia',
    available: 'Disponíveis', partial2: 'Parcial', out: 'Fora',
    sessionsToday: 'Sessões Hoje', totalNext: 'Total Próximas',
    nextSessions: 'Próximas Sessões', noSessions: 'Nenhuma sessão agendada',
    // Prep tab
    zoneConfig: [
      { key: 'optimal', label: 'Ótimo', range: '0.8–1.3' },
      { key: 'undertraining', label: 'Sub', range: '< 0.8' },
      { key: 'caution', label: 'Atenção', range: '1.3–1.5' },
      { key: 'danger', label: 'Perigo', range: '> 1.5' },
      { key: 'no_data', label: 'S/Dados', range: '—' },
    ],
    athletes: (n) => `Atletas (${n})`,
    noLoadData: 'Nenhum dado de carga disponível',
    readinessLabel: 'Readiness',
    // Periodization tab
    periodRanges: [{ key: 'week', label: 'Semana' }, { key: 'month', label: 'Mês' }, { key: 'quarter', label: 'Trimestre' }],
    blockTypes: {
      base: 'Base', build: 'Construção', peak: 'Pico', taper: 'Taper',
      recovery: 'Recuperação', competition: 'Competição', transition: 'Transição',
    },
    noBlocks: 'Nenhum bloco de periodização definido.',
    noBlocksHint: 'Blocos serão exibidos aqui quando planejados pelo treinador.',
    weeksUnit: 'semanas',
    // Manager tab
    managerSubtabs: [
      { key: 'overview', label: 'Visão Geral' }, { key: 'bookings', label: 'Instalações' },
      { key: 'travel', label: 'Viagens' }, { key: 'compliance', label: 'Compliance' },
    ],
    bookingsToday: 'Reservas Hoje', travelsUpcoming: 'Viagens',
    compliancePending: 'Exames Pendentes', complianceExpired: 'Exames Vencidos',
    todayBookings: 'Reservas de Hoje', complianceAlerts: 'Alertas de Compliance',
    conflict: 'Conflito', expired: 'Vencido', expiringSoon: 'Vencendo',
    facilities: 'Instalações', bookingsLabel: 'Reservas',
    noBookings: 'Nenhuma reserva encontrada', noTravel: 'Nenhuma viagem programada',
    noCompliance: 'Nenhum registro de compliance',
    capacity: 'Cap.', setup: 'Setup', cleanup: 'Limpeza',
    transport: 'Transporte', hotel: 'Hotel', delegation: 'Delegação', costEst: 'Custo Est.',
    compColAthlete: 'Atleta', compColType: 'Tipo', compColExpiry: 'Validade', compColStatus: 'Status',
    // PageLayout
    pageTitle: 'Agenda', pageSubtitle: 'Calendário, readiness e operações esportivas',
  },
  en: {
    views: [{ key: 'day', label: 'Day' }, { key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }],
    roleTabs: [
      { key: 'calendar', label: 'Calendar',     desc: 'Events and training' },
      { key: 'athlete',  label: 'Athlete',       desc: 'Readiness & personal ACWR' },
      { key: 'coach',    label: 'Coach',         desc: 'Roster & sessions' },
      { key: 'prep',     label: 'S&C Coach',     desc: 'Load & monitoring' },
      { key: 'manager',  label: 'Manager',       desc: 'KPIs & macro view' },
      { key: 'period',   label: 'Periodization', desc: 'Blocks & planning' },
    ],
    eventFilters: [{ id: 'all', label: 'All' }, { id: 'competition', label: 'Competitions' }, { id: 'training', label: 'Training' }],
    zoneLabels: { optimal: 'Optimal', good: 'Good', undertraining: 'Undertraining', moderate: 'Moderate', caution: 'Caution', low: 'Low', danger: 'Danger', rest: 'Rest', no_data: 'No data' },
    availLabels: { available: 'Available', partial: 'Partial', modified: 'Modified', unavailable: 'Unavailable', injured: 'Injured', illness: 'Ill', personal: 'Personal' },
    complianceLabels: { valid: 'Valid', expiring_soon: 'Expiring', expired: 'Expired', pending: 'Pending', scheduled: 'Scheduled' },
    today: 'Today', markDone: 'Mark as Done',
    aiAlerts: (n) => `AI Alerts (${n})`,
    sidebarLoad: 'Load (7 days)', sidebarSummary: 'Summary',
    sidebarLoadEmpty: 'Data available after completing training',
    totalEvents: 'Total events', completed: 'Completed', pending: 'Pending',
    noEvents: 'No events in this period',
    // Athlete tab
    morningCheckin: 'Log Morning Check-in',
    checkinTitle: 'Morning Check-in', sleepHours: 'Sleep hours',
    checkinSave: 'Log Readiness', saving: 'Saving…',
    sliders: [
      { key: 'perceived_fatigue',    label: 'Fatigue',    low: 'Fresh',  high: 'Exhausted' },
      { key: 'perceived_soreness',   label: 'Soreness',   low: 'None',   high: 'Intense' },
      { key: 'perceived_mood',       label: 'Mood',       low: 'Bad',    high: 'Great' },
      { key: 'perceived_stress',     label: 'Stress',     low: 'None',   high: 'High' },
      { key: 'perceived_motivation', label: 'Motivation', low: 'Low',    high: 'High' },
    ],
    readinessTitle: 'Readiness', acwrTitle: 'ACWR',
    readinessNoCheckin: 'Complete the morning check-in to see your readiness',
    acwrAcute: 'TSS 7d (Acute)', acwrChronic: 'TSS 28d (Chronic)',
    upcomingEvents: 'Upcoming Events', noEventsSched: 'No scheduled events',
    // Coach tab
    squadToday: 'Squad Today', dayResume: 'Day Summary',
    available: 'Available', partial2: 'Partial', out: 'Out',
    sessionsToday: 'Sessions Today', totalNext: 'Total Upcoming',
    nextSessions: 'Upcoming Sessions', noSessions: 'No sessions scheduled',
    // Prep tab
    zoneConfig: [
      { key: 'optimal', label: 'Optimal', range: '0.8–1.3' },
      { key: 'undertraining', label: 'Low', range: '< 0.8' },
      { key: 'caution', label: 'Caution', range: '1.3–1.5' },
      { key: 'danger', label: 'Danger', range: '> 1.5' },
      { key: 'no_data', label: 'No data', range: '—' },
    ],
    athletes: (n) => `Athletes (${n})`,
    noLoadData: 'No load data available',
    readinessLabel: 'Readiness',
    // Periodization tab
    periodRanges: [{ key: 'week', label: 'Week' }, { key: 'month', label: 'Month' }, { key: 'quarter', label: 'Quarter' }],
    blockTypes: {
      base: 'Base', build: 'Build', peak: 'Peak', taper: 'Taper',
      recovery: 'Recovery', competition: 'Competition', transition: 'Transition',
    },
    noBlocks: 'No periodization blocks defined.',
    noBlocksHint: 'Blocks will appear here when planned by the coach.',
    weeksUnit: 'weeks',
    // Manager tab
    managerSubtabs: [
      { key: 'overview', label: 'Overview' }, { key: 'bookings', label: 'Facilities' },
      { key: 'travel', label: 'Travel' }, { key: 'compliance', label: 'Compliance' },
    ],
    bookingsToday: 'Bookings Today', travelsUpcoming: 'Upcoming Travel',
    compliancePending: 'Pending Exams', complianceExpired: 'Expired Exams',
    todayBookings: "Today's Bookings", complianceAlerts: 'Compliance Alerts',
    conflict: 'Conflict', expired: 'Expired', expiringSoon: 'Expiring',
    facilities: 'Facilities', bookingsLabel: 'Bookings',
    noBookings: 'No bookings found', noTravel: 'No travel scheduled',
    noCompliance: 'No compliance records',
    capacity: 'Cap.', setup: 'Setup', cleanup: 'Cleanup',
    transport: 'Transport', hotel: 'Hotel', delegation: 'Party', costEst: 'Est. Cost',
    compColAthlete: 'Athlete', compColType: 'Type', compColExpiry: 'Expiry', compColStatus: 'Status',
    // PageLayout
    pageTitle: 'Agenda', pageSubtitle: 'Calendar, readiness and sports operations',
  },
};

// Alias for inline usage
const ManagerTabImported = ManagerAgendaPage;

// ════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════════════════════════════════

// VIEWS, ROLE_TABS, EVENT_FILTERS, STATUS_ZONE_COLORS, AVAILABILITY_COLORS, COMPLIANCE_COLORS
// are now driven by AGENDA_LABELS[lang] + static color maps below

const STATUS_ZONE_COLORS_STATIC = {
  optimal: { bg: '#f0fdf4', text: '#166534', dot: '#16a34a' },
  good: { bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6' },
  undertraining: { bg: '#eff6ff', text: '#1e40af', dot: '#3b82f6' },
  moderate: { bg: '#fefce8', text: '#854d0e', dot: '#eab308' },
  caution: { bg: '#fefce8', text: '#854d0e', dot: '#eab308' },
  low: { bg: '#fff7ed', text: '#9a3412', dot: '#f97316' },
  danger: { bg: '#fef2f2', text: '#991b1b', dot: '#dc2626' },
  rest: { bg: '#fef2f2', text: '#991b1b', dot: '#dc2626' },
  no_data: { bg: '#f9fafb', text: '#6b7280', dot: '#9ca3af' },
};

const AVAILABILITY_COLORS_STATIC = {
  available: { bg: '#f0fdf4', text: '#166534' },
  partial: { bg: '#fefce8', text: '#854d0e' },
  modified: { bg: '#fefce8', text: '#854d0e' },
  unavailable: { bg: '#fef2f2', text: '#991b1b' },
  injured: { bg: '#fef2f2', text: '#991b1b' },
  illness: { bg: '#fef2f2', text: '#991b1b' },
  personal: { bg: '#f9fafb', text: '#6b7280' },
};

const COMPLIANCE_COLORS_STATIC = {
  valid: { bg: '#f0fdf4', text: '#166534' },
  expiring_soon: { bg: '#fefce8', text: '#854d0e' },
  expired: { bg: '#fef2f2', text: '#991b1b' },
  pending: { bg: '#eff6ff', text: '#1e40af' },
  scheduled: { bg: '#f9fafb', text: '#6b7280' },
};

// ════════════════════════════════════════════════════════════════════════
// SHARED MICRO-COMPONENTS
// ════════════════════════════════════════════════════════════════════════

const Pill = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className="px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap"
    style={
      active
        ? { backgroundColor: VUKZ.primary, color: '#fff' }
        : { backgroundColor: VUKZ.primaryLight, color: VUKZ.textLight }
    }
  >
    {children}
  </button>
);

const StatBox = ({ value, label, accent = false }) => (
  <div
    className="rounded-xl p-3 text-center"
    style={{ backgroundColor: accent ? VUKZ.primary : VUKZ.primaryLight }}
  >
    <div
      className="text-xl font-black"
      style={{ color: accent ? '#fff' : VUKZ.text }}
    >
      {value ?? '—'}
    </div>
    <div
      className="text-[10px] font-medium mt-0.5 uppercase tracking-wider"
      style={{ color: accent ? 'rgba(255,255,255,.6)' : VUKZ.textLight }}
    >
      {label}
    </div>
  </div>
);

const ZoneBadge = ({ zone }) => {
  const { t } = useAT();
  const z = STATUS_ZONE_COLORS_STATIC[zone] || STATUS_ZONE_COLORS_STATIC.no_data;
  const label = t ? (t.zoneLabels[zone] || zone) : zone;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
      style={{ backgroundColor: z.bg, color: z.text }}
    >
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: z.dot }} />
      {label}
    </span>
  );
};

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-4">
    {Icon && <Icon className="w-4 h-4" style={{ color: VUKZ.primary }} />}
    <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: VUKZ.textLight }}>
      {children}
    </h3>
  </div>
);

const Card = ({ children, className = '' }) => (
  <div
    className={`rounded-2xl border p-5 ${className}`}
    style={{ borderColor: VUKZ.border, backgroundColor: '#fff' }}
  >
    {children}
  </div>
);

const EmptyState = ({ icon: Icon = Calendar, text }) => (
  <div
    className="rounded-2xl border-2 border-dashed p-10 text-center"
    style={{ borderColor: VUKZ.border }}
  >
    <Icon className="w-10 h-10 mx-auto mb-3" style={{ color: VUKZ.border }} />
    <p className="text-sm" style={{ color: VUKZ.textLight }}>{text}</p>
  </div>
);

// ════════════════════════════════════════════════════════════════════════
// EVENT CARD (shared across tabs)
// ════════════════════════════════════════════════════════════════════════

const EventCard = ({ event, onComplete, compact = false }) => {
  const { t, lang } = useAT();
  const locale = lang === 'en' ? 'en-US' : 'pt-BR';
  const isCompleted = event.status === 'completed';
  const startTime = event.start_datetime
    ? new Date(event.start_datetime).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : '';
  const endTime = event.end_datetime
    ? new Date(event.end_datetime).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div
      className={`rounded-xl border transition-all ${
        isCompleted ? 'opacity-60' : 'hover:shadow-md'
      }`}
      style={{ borderColor: VUKZ.border, backgroundColor: '#fff' }}
    >
      {/* Color bar */}
      <div
        className="h-1 rounded-t-xl"
        style={{ backgroundColor: event.color_hex || VUKZ.primary }}
      />

      <div className={compact ? 'p-3' : 'p-4'}>
        {/* Title row */}
        <div className="flex items-start gap-3 mb-2">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-sm"
            style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.primary }}
          >
            {event.icon || '📅'}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-sm truncate" style={{ color: VUKZ.text }}>
              {event.title}
            </h4>
            <div className="flex items-center gap-2 mt-0.5 text-xs" style={{ color: VUKZ.textLight }}>
              {startTime && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {startTime}{endTime ? ` – ${endTime}` : ''}
                </span>
              )}
              {event.venue_name && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {event.venue_name}
                </span>
              )}
            </div>
          </div>
          {isCompleted && (
            <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: VUKZ.success }} />
          )}
        </div>

        {/* TSS row (non-compact) */}
        {!compact && (event.planned_load_tss || event.actual_load_tss) && (
          <div className="grid grid-cols-3 gap-2 mt-3">
            <StatBox value={event.planned_load_tss || '—'} label="TSS Plan" />
            <StatBox value={event.actual_load_tss || '—'} label="TSS Real" />
            <StatBox value={event.volume_minutes ? `${event.volume_minutes}m` : '—'} label={lang === 'en' ? 'Duration' : 'Duração'} />
          </div>
        )}

        {/* Action */}
        {!compact && !isCompleted && onComplete && (
          <button
            onClick={() => onComplete(event)}
            className="w-full mt-3 py-2 rounded-lg text-xs font-bold transition-all hover:opacity-90"
            style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
          >
            {t ? t.markDone : 'Marcar Concluído'}
          </button>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════
// TAB: CALENDAR (main events view with day/week/month)
// ════════════════════════════════════════════════════════════════════════

function CalendarTab({
  events, loading, loadData, alerts, view, currentDate,
  onPrev, onNext, onToday, onViewChange, activeFilter, onFilterChange, onComplete,
}) {
  const { t, lang } = useAT();
  const locale = lang === 'en' ? 'en-US' : 'pt-BR';
  const VIEWS = t.views;
  const EVENT_FILTERS = t.eventFilters.map((f, i) => ({ ...f, icon: [Calendar, Zap, Activity][i] }));
  const grouped = useMemo(() => groupEventsByDate(events), [events]);
  const sortedDates = Object.keys(grouped).sort();

  return (
    <div className="space-y-6">
      {/* ── Controls bar ─────────────────────────────────── */}
      <div
        className="rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3"
        style={{ borderColor: VUKZ.border, backgroundColor: '#fff' }}
      >
        {/* Date nav */}
        <div className="flex items-center gap-1">
          <button onClick={onPrev} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-4 h-4" style={{ color: VUKZ.text }} />
          </button>
          <span className="text-sm font-bold min-w-[140px] text-center" style={{ color: VUKZ.text }}>
            {currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
          </span>
          <button onClick={onNext} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ChevronRight className="w-4 h-4" style={{ color: VUKZ.text }} />
          </button>
          <button
            onClick={onToday}
            className="ml-1 px-3 py-1.5 rounded-lg text-xs font-bold"
            style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.text }}
          >
            {t.today}
          </button>
        </div>

        {/* View toggle */}
        <div className="flex gap-1">
          {VIEWS.map((v) => (
            <Pill key={v.key} active={view === v.key} onClick={() => onViewChange(v.key)}>
              {v.label}
            </Pill>
          ))}
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-1">
          <Filter className="w-3.5 h-3.5" style={{ color: VUKZ.textLight }} />
          {EVENT_FILTERS.map((f) => (
            <Pill key={f.id} active={activeFilter === f.id} onClick={() => onFilterChange(f.id)}>
              {f.label}
            </Pill>
          ))}
        </div>
      </div>

      {/* ── AI Alerts ────────────────────────────────────── */}
      {alerts.length > 0 && (
        <Card>
          <SectionTitle icon={AlertCircle}>{t.aiAlerts(alerts.length)}</SectionTitle>
          <div className="space-y-2">
            {alerts.map((a, i) => (
              <div
                key={i}
                className="rounded-xl border p-3 flex items-start gap-3"
                style={{
                  borderColor:
                    a.severity === 'critical' ? '#fecaca' :
                    a.severity === 'warning' ? '#fed7aa' : VUKZ.border,
                  backgroundColor:
                    a.severity === 'critical' ? '#fef2f2' :
                    a.severity === 'warning' ? '#fff7ed' : '#f9fafb',
                }}
              >
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{
                  color: a.severity === 'critical' ? '#dc2626' :
                         a.severity === 'warning' ? '#f59e0b' : VUKZ.textLight,
                }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: VUKZ.text }}>
                    {a.alert_message}
                  </p>
                  {a.suggested_action && (
                    <p className="text-xs mt-1" style={{ color: VUKZ.textLight }}>
                      💡 {a.suggested_action}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Events + Sidebar ─────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Events list (2/3) */}
        <div className="lg:col-span-2 space-y-5">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-40 rounded-2xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
              ))}
            </div>
          ) : sortedDates.length === 0 ? (
            <EmptyState text={t.noEvents} />
          ) : (
            sortedDates.map((dateKey) => {
              const dayEvents = grouped[dateKey];
              const d = new Date(dateKey + 'T12:00:00');
              const isToday = d.toDateString() === new Date().toDateString();
              return (
                <div key={dateKey}>
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex flex-col items-center justify-center text-center flex-shrink-0"
                      style={{
                        backgroundColor: isToday ? VUKZ.primary : VUKZ.primaryLight,
                        color: isToday ? '#fff' : VUKZ.text,
                      }}
                    >
                      <span className="text-xs font-bold leading-none">
                        {d.toLocaleDateString(locale, { weekday: 'short' }).replace('.', '')}
                      </span>
                      <span className="text-sm font-black leading-none">{d.getDate()}</span>
                    </div>
                    <div>
                      <span className="text-sm font-bold" style={{ color: VUKZ.text }}>
                        {d.toLocaleDateString(locale, { day: '2-digit', month: 'long' })}
                      </span>
                      {isToday && (
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-widest" style={{ color: VUKZ.textLight }}>
                          {t.today}
                        </span>
                      )}
                    </div>
                  </div>
                  {dayEvents.map((ev) => (
                    <EventCard key={ev.event_id || ev.id} event={ev} onComplete={onComplete} />
                  ))}
                </div>
              );
            })
          )}
        </div>

        {/* Sidebar (1/3) */}
        <div className="space-y-5">
          {/* Load chart */}
          <Card>
            <SectionTitle icon={BarChart3}>{t.sidebarLoad}</SectionTitle>
            {loadData.length === 0 ? (
              <p className="text-xs text-center py-4" style={{ color: VUKZ.textLight }}>
                {t.sidebarLoadEmpty}
              </p>
            ) : (
              <div className="space-y-2">
                {loadData.slice(0, 7).reverse().map((day, i) => {
                  const maxTSS = Math.max(...loadData.slice(0, 7).map((d) => d.rolling_7d_tss || 0), 1);
                  const pct = ((day.rolling_7d_tss || 0) / maxTSS) * 100;
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-[10px] w-10 text-right font-medium" style={{ color: VUKZ.textLight }}>
                        {new Date(day.tracking_date).toLocaleDateString(locale, { day: '2-digit', month: 'short' })}
                      </span>
                      <div className="flex-1 h-5 rounded-full overflow-hidden" style={{ backgroundColor: VUKZ.primaryLight }}>
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${pct}%`,
                            backgroundColor:
                              day.load_status === 'overload' ? '#dc2626' :
                              day.load_status === 'optimal' ? '#16a34a' : VUKZ.primary,
                          }}
                        />
                      </div>
                      <span className="text-[10px] w-10 font-bold" style={{ color: VUKZ.text }}>
                        {Math.round(day.rolling_7d_tss || 0)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Quick stats */}
          <Card>
            <SectionTitle icon={BarChart3}>{t.sidebarSummary}</SectionTitle>
            <div className="space-y-2.5">
              {[
                { label: t.totalEvents, value: events.length },
                { label: t.completed, value: events.filter((e) => e.status === 'completed').length, color: VUKZ.success },
                { label: t.pending, value: events.filter((e) => e.status === 'scheduled').length, color: '#f59e0b' },
              ].map((s) => (
                <div key={s.label} className="flex justify-between items-center">
                  <span className="text-xs" style={{ color: VUKZ.textLight }}>{s.label}</span>
                  <span className="text-sm font-bold" style={{ color: s.color || VUKZ.text }}>{s.value}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// TAB: ATHLETE (readiness + ACWR + events)
// ════════════════════════════════════════════════════════════════════════

function AthleteTab() {
  const { t, lang } = useAT();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkinForm, setCheckinForm] = useState({
    sleep_hours: '', perceived_fatigue: 3, perceived_soreness: 3,
    perceived_mood: 3, perceived_stress: 3, perceived_motivation: 3,
  });
  const userId = 1;

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const { data } = await AZURE.get('/agenda/role/athlete/dashboard', { params: { user_id: userId } });
      if (data.success) {
        setDashboard(data.data);
        if (!data.data.readiness) setShowCheckin(true);
      }
    } catch (err) {
      console.error('[Agenda:Athlete] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const handleReadinessSubmit = async () => {
    setCheckinLoading(true);
    try {
      await AZURE.post('/agenda/role/athlete/readiness', {
        athlete_id: userId, ...checkinForm,
        sleep_hours: checkinForm.sleep_hours ? parseFloat(checkinForm.sleep_hours) : null,
      });
      setShowCheckin(false);
      fetchDashboard();
    } catch (err) {
      console.error('[Agenda:Athlete] Readiness error:', err);
    } finally {
      setCheckinLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-44 rounded-2xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
        ))}
      </div>
    );
  }

  const readiness = dashboard?.readiness;
  const readinessZone = STATUS_ZONE_COLORS_STATIC[readiness?.readiness_status] || STATUS_ZONE_COLORS_STATIC.no_data;
  const acwrZone = STATUS_ZONE_COLORS_STATIC[dashboard?.acwr_zone] || STATUS_ZONE_COLORS_STATIC.no_data;

  const sliders = t ? t.sliders : [
    { key: 'perceived_fatigue',    label: 'Fadiga',     low: 'Fresco', high: 'Exausto' },
    { key: 'perceived_soreness',   label: 'Dor Muscular', low: 'Nenhuma', high: 'Intensa' },
    { key: 'perceived_mood',       label: 'Humor',      low: 'Ruim',  high: 'Ótimo' },
    { key: 'perceived_stress',     label: 'Estresse',   low: 'Nenhum', high: 'Alto' },
    { key: 'perceived_motivation', label: 'Motivação',  low: 'Baixa', high: 'Alta' },
  ];

  return (
    <div className="space-y-6">
      {/* Check-in CTA */}
      {!showCheckin && !readiness && (
        <button
          onClick={() => setShowCheckin(true)}
          className="w-full py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90"
          style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
        >
          ☀️ {t ? t.morningCheckin : 'Registrar Check-in Matinal'}
        </button>
      )}

      {/* Check-in form */}
      {showCheckin && (
        <Card>
          <SectionTitle icon={Heart}>{t ? t.checkinTitle : 'Check-in Matinal'}</SectionTitle>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold mb-1 block" style={{ color: VUKZ.text }}>{t ? t.sleepHours : 'Horas de sono'}</label>
              <input
                type="number" step="0.5" min="0" max="14"
                value={checkinForm.sleep_hours}
                onChange={(e) => setCheckinForm({ ...checkinForm, sleep_hours: e.target.value })}
                placeholder="7.5"
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: VUKZ.border, color: VUKZ.text }}
              />
            </div>
            {sliders.map((s) => (
              <div key={s.key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-semibold" style={{ color: VUKZ.text }}>{s.label}</span>
                  <span style={{ color: VUKZ.textLight }}>{checkinForm[s.key]}/5</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] w-12" style={{ color: VUKZ.textLight }}>{s.low}</span>
                  <input
                    type="range" min="1" max="5"
                    value={checkinForm[s.key]}
                    onChange={(e) => setCheckinForm({ ...checkinForm, [s.key]: parseInt(e.target.value) })}
                    className="flex-1 accent-neutral-900"
                  />
                  <span className="text-[10px] w-12 text-right" style={{ color: VUKZ.textLight }}>{s.high}</span>
                </div>
              </div>
            ))}
            <button
              onClick={handleReadinessSubmit}
              disabled={checkinLoading}
              className="w-full py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
            >
              {checkinLoading ? (t ? t.saving : 'Salvando…') : (t ? t.checkinSave : 'Registrar Readiness')}
            </button>
          </div>
        </Card>
      )}

      {/* Readiness + ACWR */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Readiness */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <SectionTitle icon={Heart}>{t ? t.readinessTitle : 'Readiness'}</SectionTitle>
            {readiness && <ZoneBadge zone={readiness.readiness_status} />}
          </div>
          {readiness ? (
            <>
              <div className="text-4xl font-black mb-2" style={{ color: VUKZ.primary }}>
                {readiness.readiness_score}<span className="text-lg font-medium">/100</span>
              </div>
              <p className="text-xs mb-4" style={{ color: VUKZ.textLight }}>
                {readiness.ai_recommendation}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {readiness.sleep_hours && <StatBox value={`${readiness.sleep_hours}h`} label={t ? t.statSleep || 'Sono' : 'Sono'} />}
                {readiness.perceived_fatigue && <StatBox value={`${readiness.perceived_fatigue}/5`} label={t ? t.statFatigue || 'Fadiga' : 'Fadiga'} />}
                {readiness.hrv_ms && <StatBox value={readiness.hrv_ms} label={t ? t.statHrv || 'HRV (ms)' : 'HRV (ms)'} />}
              </div>
            </>
          ) : (
            <p className="text-xs py-4 text-center" style={{ color: VUKZ.textLight }}>
              {t ? t.readinessNoCheckin : 'Complete o check-in matinal para ver seu readiness'}
            </p>
          )}
        </Card>

        {/* ACWR */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <SectionTitle icon={BarChart3}>{t ? t.acwrTitle : 'ACWR'}</SectionTitle>
            <ZoneBadge zone={dashboard?.acwr_zone || 'no_data'} />
          </div>
          <div className="text-4xl font-black mb-2" style={{ color: VUKZ.primary }}>
            {dashboard?.acwr ?? '—'}
          </div>
          <p className="text-xs mb-4" style={{ color: VUKZ.textLight }}>
            {t ? (t.zoneLabels[dashboard?.acwr_zone] || '') : acwrZone.label}
          </p>
          {dashboard?.load && (
            <div className="grid grid-cols-2 gap-2">
              <StatBox value={dashboard.load.rolling_7d_tss || 0} label={t ? t.acwrAcute : 'TSS 7d (Agudo)'} />
              <StatBox value={dashboard.load.rolling_28d_tss || 0} label={t ? t.acwrChronic : 'TSS 28d (Crônico)'} />
            </div>
          )}
        </Card>
      </div>

      {/* Upcoming events */}
      <div>
        <SectionTitle icon={Calendar}>{t ? t.upcomingEvents : 'Próximos Eventos'}</SectionTitle>
        {dashboard?.events?.length > 0 ? (
          <div className="space-y-3">
            {dashboard.events.map((ev, i) => (
              <EventCard key={ev.event_id || i} event={ev} compact />
            ))}
          </div>
        ) : (
          <EmptyState text={t ? t.noEventsSched : 'Nenhum evento agendado'} />
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// TAB: COACH (squad + sessions)
// ════════════════════════════════════════════════════════════════════════

function CoachTab() {
  const { t } = useAT();
  const [dashboard, setDashboard] = useState(null);
  const [squadData, setSquadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const coachId = 1;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [dashRes, squadRes] = await Promise.all([
          AZURE.get('/agenda/role/coach/dashboard', { params: { coach_id: coachId } }),
          AZURE.get('/agenda/role/coach/squad-availability'),
        ]);
        if (dashRes.data.success) setDashboard(dashRes.data.data);
        if (squadRes.data.success) setSquadData(squadRes.data.data);
      } catch (err) {
        console.error('[Agenda:Coach] Error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-44 rounded-2xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
        ))}
      </div>
    );
  }

  const summary = squadData?.summary || {};
  const athletes = squadData?.athletes || [];
  const total = summary.total || 0;

  return (
    <div className="space-y-6">
      {/* Squad + Day stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Squad availability */}
        <Card>
          <SectionTitle icon={Users}>{t ? t.squadToday : 'Elenco Hoje'}</SectionTitle>
          {/* Availability bar */}
          {total > 0 && (
            <div className="flex gap-0.5 h-3 rounded-full overflow-hidden mb-4" style={{ backgroundColor: VUKZ.primaryLight }}>
              <div style={{ width: `${((summary.available || 0) / total) * 100}%`, backgroundColor: '#16a34a' }} />
              <div style={{ width: `${((summary.partial || 0) / total) * 100}%`, backgroundColor: '#eab308' }} />
              <div style={{ width: `${((summary.unavailable || 0) / total) * 100}%`, backgroundColor: '#dc2626' }} />
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <StatBox value={summary.available || 0} label={t ? t.available : 'Disponíveis'} />
            <StatBox value={summary.partial || 0} label={t ? t.partial2 : 'Parcial'} />
            <StatBox value={summary.unavailable || 0} label={t ? t.out : 'Fora'} />
          </div>
          {/* Athlete list */}
          {athletes.length > 0 && (
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {athletes.map((a, i) => {
                const c = AVAILABILITY_COLORS_STATIC[a.status] || AVAILABILITY_COLORS_STATIC.available;
                const availLabel = t ? (t.availLabels[a.status] || a.status) : (a.status);
                return (
                  <div key={i} className="flex items-center justify-between py-1.5 px-3 rounded-lg" style={{ backgroundColor: VUKZ.primaryLight }}>
                    <span className="text-xs font-medium" style={{ color: VUKZ.text }}>
                      {a.athlete_name || `Atleta #${a.athlete_id}`}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: c.bg, color: c.text }}>
                      {availLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Day stats */}
        <Card>
          <SectionTitle icon={BarChart3}>{t ? t.dayResume : 'Resumo do Dia'}</SectionTitle>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <StatBox value={dashboard?.total_sessions_today || 0} label={t ? t.sessionsToday : 'Sessões Hoje'} accent />
            <StatBox value={dashboard?.events?.length || 0} label={t ? t.totalNext : 'Total Próximas'} />
          </div>
          {dashboard?.squad_summary?.length > 0 && (
            <div className="space-y-1.5">
              {dashboard.squad_summary.map((s, i) => (
                <div key={i} className="flex justify-between text-xs py-1 border-b" style={{ borderColor: VUKZ.primaryLight }}>
                  <span className="capitalize" style={{ color: VUKZ.textLight }}>{s.status}</span>
                  <span className="font-bold" style={{ color: VUKZ.text }}>{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Sessions */}
      <div>
        <SectionTitle icon={Calendar}>{t ? t.nextSessions : 'Próximas Sessões'}</SectionTitle>
        {dashboard?.events?.length > 0 ? (
          <div className="space-y-3">
            {dashboard.events.map((ev, i) => (
              <EventCard key={ev.event_id || i} event={ev} compact />
            ))}
          </div>
        ) : (
          <EmptyState text={t ? t.noSessions : 'Nenhuma sessão agendada'} />
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// TAB: PREP (load monitoring + ACWR)
// ════════════════════════════════════════════════════════════════════════

function PrepTab() {
  const { t } = useAT();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [sortBy, setSortBy] = useState('acwr_zone');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await AZURE.get('/agenda/role/prep/dashboard');
        if (data.success) setDashboard(data.data);
      } catch (err) {
        console.error('[Agenda:Prep] Error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
          ))}
        </div>
        <div className="h-48 rounded-2xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
      </div>
    );
  }

  const zones = dashboard?.zones_summary || {};
  const zoneConfig = t ? t.zoneConfig : [
    { key: 'optimal',       label: 'Ótimo', range: '0.8–1.3' },
    { key: 'undertraining', label: 'Sub',   range: '< 0.8' },
    { key: 'caution',       label: 'Atenção', range: '1.3–1.5' },
    { key: 'danger',        label: 'Perigo',  range: '> 1.5' },
    { key: 'no_data',       label: 'S/Dados', range: '—' },
  ];

  const sortedAthletes = (dashboard?.athletes || []).sort((a, b) => {
    if (sortBy === 'acwr_zone') {
      const priority = { danger: 0, caution: 1, no_data: 2, undertraining: 3, optimal: 4 };
      return (priority[a.acwr_zone] ?? 5) - (priority[b.acwr_zone] ?? 5);
    }
    return (a.readiness_score ?? 999) - (b.readiness_score ?? 999);
  });

  return (
    <div className="space-y-6">
      {/* Zone summary */}
      <div className="grid grid-cols-5 gap-2">
        {zoneConfig.map((z) => {
          const col = STATUS_ZONE_COLORS_STATIC[z.key] || STATUS_ZONE_COLORS_STATIC.no_data;
          return (
            <div key={z.key} className="rounded-xl p-3 text-center" style={{ backgroundColor: col.bg }}>
              <div className="text-2xl font-black" style={{ color: col.text }}>
                {zones[z.key] || 0}
              </div>
              <div className="text-[10px] font-bold mt-1" style={{ color: col.text }}>{z.label}</div>
              <div className="text-[9px] mt-0.5 opacity-60" style={{ color: col.text }}>{z.range}</div>
            </div>
          );
        })}
      </div>

      {/* Sort controls */}
      <div className="flex items-center justify-between">
        <SectionTitle icon={Users}>{t ? t.athletes(sortedAthletes.length) : `Atletas (${sortedAthletes.length})`}</SectionTitle>
        <div className="flex gap-1">
          <Pill active={sortBy === 'acwr_zone'} onClick={() => setSortBy('acwr_zone')}>ACWR</Pill>
          <Pill active={sortBy === 'readiness'} onClick={() => setSortBy('readiness')}>Readiness</Pill>
        </div>
      </div>

      {/* Athletes */}
      {sortedAthletes.length > 0 ? (
        <div className="space-y-2">
          {sortedAthletes.map((a, i) => (
            <Card key={a.athlete_id || i} className="cursor-pointer hover:shadow-md" onClick={() => setSelectedAthlete(a.athlete_id)}>
              <div className="flex items-center gap-4">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ backgroundColor: VUKZ.primary, color: '#fff' }}
                >
                  {a.athlete_id}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold" style={{ color: VUKZ.text }}>
                    Atleta #{a.athlete_id}
                  </h4>
                  <div className="flex items-center gap-2 mt-1">
                    <ZoneBadge zone={a.acwr_zone} />
                    {a.readiness_score != null && (
                      <span className="text-[10px]" style={{ color: VUKZ.textLight }}>
                        {t ? t.readinessLabel : 'Readiness'}: {a.readiness_score}/100
                      </span>
                    )}
                    {a.active_alerts > 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
                        ⚠ {a.active_alerts}
                      </span>
                    )}
                  </div>
                </div>
                <div className="hidden sm:grid grid-cols-3 gap-3 text-[10px] text-center">
                  <div>
                    <div className="font-bold" style={{ color: VUKZ.text }}>{a.actual_tss || 0}</div>
                    <div style={{ color: VUKZ.textLight }}>{t ? t.today : 'Hoje'}</div>
                  </div>
                  <div>
                    <div className="font-bold" style={{ color: VUKZ.text }}>{a.rolling_7d_tss || 0}</div>
                    <div style={{ color: VUKZ.textLight }}>7d</div>
                  </div>
                  <div>
                    <div className="font-bold" style={{ color: VUKZ.text }}>{a.rolling_28d_tss || 0}</div>
                    <div style={{ color: VUKZ.textLight }}>28d</div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState icon={Users} text={t ? t.noLoadData : 'Nenhum dado de carga disponível'} />
      )}

      {/* ACWR Detail Modal */}
      {selectedAthlete && (
        <ACWRModal athleteId={selectedAthlete} onClose={() => setSelectedAthlete(null)} />
      )}
    </div>
  );
}

function ACWRModal({ athleteId, onClose }) {
  const { t, lang } = useAT();
  const locale = lang === 'en' ? 'en-US' : 'pt-BR';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await AZURE.get(`/agenda/role/prep/acwr/${athleteId}`);
        if (res.success) setData(res.data);
      } catch (err) { console.error('[Agenda:ACWR] Error:', err); }
      finally { setLoading(false); }
    })();
  }, [athleteId]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6"
        style={{ backgroundColor: '#fff' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold" style={{ color: VUKZ.text }}>
            {t ? `ACWR — ${lang === 'en' ? 'Athlete' : 'Atleta'} #${athleteId}` : `ACWR — Atleta #${athleteId}`}
          </h3>
          <button onClick={onClose} className="text-xl" style={{ color: VUKZ.textLight }}>✕</button>
        </div>
        {loading ? (
          <div className="space-y-3">
            <div className="h-20 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
            <div className="h-40 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
          </div>
        ) : data ? (
          <>
            {data.current_readiness && (
              <Card className="mb-4">
                <div className="flex justify-between text-xs">
                  <span style={{ color: VUKZ.textLight }}>{t ? (lang === 'en' ? 'Current Readiness' : 'Readiness Atual') : 'Readiness Atual'}</span>
                  <span className="font-bold" style={{ color: VUKZ.text }}>{data.current_readiness.readiness_score}/100</span>
                </div>
                <p className="text-xs mt-1" style={{ color: VUKZ.textLight }}>{data.current_readiness.ai_recommendation}</p>
              </Card>
            )}
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {data.load_history?.slice(0, 14).map((row, i) => (
                <div key={i} className="flex justify-between text-xs py-1 border-b" style={{ borderColor: VUKZ.primaryLight }}>
                  <span style={{ color: VUKZ.textLight }}>{new Date(row.tracking_date).toLocaleDateString(locale)}</span>
                  <span className="font-medium" style={{ color: VUKZ.text }}>TSS: {row.actual_tss || 0} | ACWR: {row.acwr_ratio ?? '—'}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-center py-8 text-xs" style={{ color: VUKZ.textLight }}>{t ? (lang === 'en' ? 'No data' : 'Nenhum dado') : 'Nenhum dado'}</p>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// TAB: PERIODIZAÇÃO (blocks, load planning, templates)
// ════════════════════════════════════════════════════════════════════════

function PeriodizationTab() {
  const { t, lang } = useAT();
  const locale = lang === 'en' ? 'en-US' : 'pt-BR';
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('month'); // week | month | quarter

  const BLOCK_TYPES = {
    base:  { label: t ? t.blockTypes.base  : 'Base',        color: '#3b82f6', bg: '#eff6ff' },
    build: { label: t ? t.blockTypes.build : 'Construção',  color: '#f59e0b', bg: '#fef3c7' },
    peak:  { label: t ? t.blockTypes.peak  : 'Pico',         color: '#ef4444', bg: '#fef2f2' },
    taper: { label: t ? t.blockTypes.taper : 'Taper', color: '#8b5cf6', bg: '#f5f3ff' },
    recovery: { label: t ? t.blockTypes.recovery : 'Recuperação', color: '#10b981', bg: '#ecfdf5' },
    competition: { label: t ? t.blockTypes.competition : 'Competição', color: '#ec4899', bg: '#fdf2f8' },
    transition: { label: t ? t.blockTypes.transition : 'Transição', color: '#6b7280', bg: '#f9fafb' },
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await AZURE.get('/schedule/periodization/blocks', { params: { range } });
        setBlocks(res.data?.data || res.data?.blocks || []);
      } catch (err) {
        console.error('[Periodization] Error:', err);
        setBlocks([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [range]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Range selector */}
      <div className="flex gap-2">
        {(t ? t.periodRanges : [{ key: 'week', label: 'Semana' }, { key: 'month', label: 'Mês' }, { key: 'quarter', label: 'Trimestre' }]).map(r => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
              range === r.key ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            style={range === r.key ? { backgroundColor: VUKZ.primary } : {}}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Blocks */}
      {blocks.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{t ? t.noBlocks : 'Nenhum bloco de periodização definido.'}</p>
          <p className="text-xs mt-1">{t ? t.noBlocksHint : 'Blocos serão exibidos aqui quando planejados pelo treinador.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {blocks.map((block, i) => {
            const type = BLOCK_TYPES[block.type] || BLOCK_TYPES.base;
            const startDate = block.start_date ? new Date(block.start_date).toLocaleDateString(locale, { day: '2-digit', month: 'short' }) : '';
            const endDate = block.end_date ? new Date(block.end_date).toLocaleDateString(locale, { day: '2-digit', month: 'short' }) : '';

            return (
              <div
                key={block.id || i}
                className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4"
              >
                {/* Color indicator */}
                <div className="w-2 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: type.color }} />

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold" style={{ color: VUKZ.text }}>
                      {block.name || type.label}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded text-[10px] font-bold"
                      style={{ backgroundColor: type.bg, color: type.color }}
                    >
                      {type.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {startDate}{endDate ? ` → ${endDate}` : ''}
                    {block.weeks && <span className="ml-2">• {block.weeks} {t ? t.weeksUnit : 'semanas'}</span>}
                  </p>
                  {block.focus && (
                    <p className="text-xs text-gray-400 mt-0.5 truncate">{block.focus}</p>
                  )}
                </div>

                {/* Load indicator */}
                {block.target_tss && (
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold" style={{ color: type.color }}>{block.target_tss}</p>
                    <p className="text-[10px] text-gray-400">{lang === 'en' ? 'TSS/week' : 'TSS/sem'}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// TAB: MANAGER (LEGACY — kept for reference, now uses ManagerTabImported)
// ════════════════════════════════════════════════════════════════════════

function ManagerTab() {
  const { t, lang } = useAT();
  const locale = lang === 'en' ? 'en-US' : 'pt-BR';
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [travels, setTravels] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [loading, setLoading] = useState(true);

  const MANAGER_SUBTABS = (t ? t.managerSubtabs : [
    { key: 'overview', label: 'Visão Geral' }, { key: 'bookings', label: 'Instalações' },
    { key: 'travel', label: 'Viagens' }, { key: 'compliance', label: 'Compliance' },
  ]).map((s, i) => ({ ...s, icon: [Eye, Building2, Plane, Shield][i] }));

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [dashRes, facRes, bookRes, travelRes, compRes] = await Promise.all([
          AZURE.get('/agenda/role/manager/dashboard'),
          AZURE.get('/agenda/role/manager/facilities'),
          AZURE.get('/agenda/role/manager/bookings'),
          AZURE.get('/agenda/role/manager/travel'),
          AZURE.get('/agenda/role/manager/compliance'),
        ]);
        if (dashRes.data.success) setDashboard(dashRes.data.data);
        if (facRes.data.success) setFacilities(facRes.data.data || []);
        if (bookRes.data.success) setBookings(bookRes.data.data || []);
        if (travelRes.data.success) setTravels(travelRes.data.data || []);
        if (compRes.data.success) setCompliance(compRes.data.data || []);
      } catch (err) {
        console.error('[Agenda:Manager] Error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
        ))}
      </div>
    );
  }

  const stats = dashboard?.stats || {};

  return (
    <div className="space-y-6">
      {/* Sub-tab bar */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {MANAGER_SUBTABS.map((t) => (
          <Pill key={t.key} active={activeTab === t.key} onClick={() => setActiveTab(t.key)}>
            <span className="flex items-center gap-1.5">
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </span>
          </Pill>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox value={stats.bookings_count || 0} label={t ? t.bookingsToday : 'Reservas Hoje'} accent />
            <StatBox value={stats.travel_upcoming || 0} label={t ? t.travelsUpcoming : 'Viagens'} />
            <StatBox value={stats.compliance_pending || 0} label={t ? t.compliancePending : 'Exames Pendentes'} />
            <StatBox value={stats.compliance_expired || 0} label={t ? t.complianceExpired : 'Exames Vencidos'} />
          </div>

          {/* Today bookings */}
          {dashboard?.bookings_today?.length > 0 && (
            <Card>
              <SectionTitle icon={Building2}>{t ? t.todayBookings : 'Reservas de Hoje'}</SectionTitle>
              <div className="space-y-1.5">
                {dashboard.bookings_today.map((b, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ backgroundColor: VUKZ.primaryLight }}>
                    <div>
                      <span className="text-xs font-bold" style={{ color: VUKZ.text }}>{b.facility_name}</span>
                      <span className="text-[10px] ml-2" style={{ color: VUKZ.textLight }}>
                        {b.start_datetime && new Date(b.start_datetime).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                        {' – '}
                        {b.end_datetime && new Date(b.end_datetime).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {b.conflicting_bookings > 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }}>
                        {t ? t.conflict : 'Conflito'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Compliance alerts */}
          {dashboard?.compliance_alerts?.length > 0 && (
            <Card>
              <SectionTitle icon={Shield}>{t ? t.complianceAlerts : 'Alertas de Compliance'}</SectionTitle>
              <div className="space-y-1.5">
                {dashboard.compliance_alerts.slice(0, 5).map((c, i) => {
                  const isExpired = c.status === 'expired';
                  return (
                    <div
                      key={i}
                      className="flex items-center justify-between py-2 px-3 rounded-lg"
                      style={{ backgroundColor: isExpired ? '#fef2f2' : '#fefce8' }}
                    >
                      <div>
                        <span className="text-xs font-bold" style={{ color: VUKZ.text }}>
                          {c.athlete_name || `Atleta #${c.athlete_id}`}
                        </span>
                        <span className="text-[10px] ml-2 capitalize" style={{ color: VUKZ.textLight }}>
                          {c.compliance_type?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{
                          backgroundColor: isExpired ? '#fecaca' : '#fef3c7',
                          color: isExpired ? '#991b1b' : '#854d0e',
                        }}
                      >
                        {isExpired ? (t ? t.expired : 'Vencido') : (t ? t.expiringSoon : 'Vencendo')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Bookings */}
      {activeTab === 'bookings' && (
        <div className="space-y-5">
          {facilities.length > 0 && (
            <Card>
              <SectionTitle icon={Building2}>{t ? t.facilities : 'Instalações'}</SectionTitle>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {facilities.map((f, i) => (
                  <div key={i} className="rounded-xl p-4" style={{ backgroundColor: VUKZ.primaryLight }}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center text-sm" style={{ backgroundColor: VUKZ.primary, color: '#fff' }}>
                        {f.facility_type === 'pool' ? '🏊' : f.facility_type === 'field' ? '⚽' : '🏢'}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold" style={{ color: VUKZ.text }}>{f.facility_name}</h4>
                        <p className="text-[10px] capitalize" style={{ color: VUKZ.textLight }}>{f.facility_type}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <StatBox value={f.capacity_athletes} label={t ? t.capacity : 'Cap.'} />
                      <StatBox value={`${f.setup_time_min}m`} label={t ? t.setup : 'Setup'} />
                      <StatBox value={`${f.cleanup_time_min}m`} label={t ? t.cleanup : 'Limpeza'} />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {bookings.length > 0 ? (
            <Card>
              <SectionTitle icon={Calendar}>{t ? t.bookingsLabel : 'Reservas'}</SectionTitle>
              <div className="space-y-1.5">
                {bookings.map((b, i) => (
                  <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ backgroundColor: VUKZ.primaryLight }}>
                    <div>
                      <span className="text-xs font-bold" style={{ color: VUKZ.text }}>{b.facility_name}</span>
                      <span className="text-[10px] ml-2" style={{ color: VUKZ.textLight }}>{b.sport}</span>
                    </div>
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: b.booking_status === 'confirmed' ? '#f0fdf4' : VUKZ.primaryLight,
                        color: b.booking_status === 'confirmed' ? '#166534' : VUKZ.textLight,
                      }}
                    >
                      {b.booking_status}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          ) : (
            <EmptyState icon={Building2} text={t ? t.noBookings : 'Nenhuma reserva encontrada'} />
          )}
        </div>
      )}

      {/* Travel */}
      {activeTab === 'travel' && (
        <div>
          {travels.length > 0 ? (
            <div className="space-y-3">
              {travels.map((t, i) => (
                <Card key={i}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-bold" style={{ color: VUKZ.text }}>{t.destination}</h4>
                      <p className="text-[10px]" style={{ color: VUKZ.textLight }}>
                        {t.departure_datetime && new Date(t.departure_datetime).toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' })}
                        {t.return_datetime && ` → ${new Date(t.return_datetime).toLocaleDateString(locale, { day: '2-digit', month: 'long' })}`}
                      </p>
                    </div>
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full capitalize"
                      style={{
                        backgroundColor: t.status === 'confirmed' ? '#f0fdf4' : VUKZ.primaryLight,
                        color: t.status === 'confirmed' ? '#166534' : VUKZ.textLight,
                      }}
                    >
                      {t.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <StatBox value={t.transport_type || '—'} label={lang === 'en' ? 'Transport' : 'Transporte'} />
                    <StatBox value={t.hotel_name || '—'} label="Hotel" />
                    <StatBox value={t.total_headcount || '—'} label={lang === 'en' ? 'Party' : 'Delegação'} />
                    <StatBox value={t.total_estimated_cost ? `R$${(t.total_estimated_cost / 1000).toFixed(0)}k` : '—'} label={lang === 'en' ? 'Est. Cost' : 'Custo Est.'} />
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <EmptyState icon={Plane} text={t ? t.noTravel : 'Nenhuma viagem programada'} />
          )}
        </div>
      )}

      {/* Compliance */}
      {activeTab === 'compliance' && (
        <div>
          {compliance.length > 0 ? (
            <Card>
              <SectionTitle icon={Shield}>Compliance Médico</SectionTitle>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b" style={{ borderColor: VUKZ.border }}>
                      <th className="text-left py-2 px-2 font-bold" style={{ color: VUKZ.textLight }}>{t ? t.compColAthlete : 'Atleta'}</th>
                      <th className="text-left py-2 px-2 font-bold" style={{ color: VUKZ.textLight }}>{t ? t.compColType : 'Tipo'}</th>
                      <th className="text-left py-2 px-2 font-bold" style={{ color: VUKZ.textLight }}>{t ? t.compColExpiry : 'Validade'}</th>
                      <th className="text-left py-2 px-2 font-bold" style={{ color: VUKZ.textLight }}>{t ? t.compColStatus : 'Status'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compliance.map((c, i) => {
                      const s = COMPLIANCE_COLORS_STATIC[c.status] || COMPLIANCE_COLORS_STATIC.pending;
                      const compLabel = t ? (t.complianceLabels[c.status] || c.status) : (c.status);
                      return (
                        <tr key={i} className="border-b hover:bg-gray-50" style={{ borderColor: VUKZ.primaryLight }}>
                          <td className="py-2 px-2 font-medium" style={{ color: VUKZ.text }}>{c.athlete_name || `#${c.athlete_id}`}</td>
                          <td className="py-2 px-2 capitalize" style={{ color: VUKZ.textLight }}>{c.compliance_type?.replace(/_/g, ' ')}</td>
                          <td className="py-2 px-2" style={{ color: VUKZ.textLight }}>{c.expiry_date ? new Date(c.expiry_date).toLocaleDateString(locale) : '—'}</td>
                          <td className="py-2 px-2">
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.bg, color: s.text }}>{compLabel}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            <EmptyState icon={Shield} text={t ? t.noCompliance : 'Nenhum registro de compliance'} />
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ════════════════════════════════════════════════════════════════════════

export default function UnifiedAgendaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filters = parseAgendaQuery(searchParams);
  const { lang } = useGovernanceLang();
  const t = AGENDA_LABELS[lang] || AGENDA_LABELS.pt;
  const { activeRole } = useRBAC();
  const { user } = useAuth();

  // Map RBAC role → agenda tab key
  const roleToTabKey = {
    ATLETA:           'athlete',
    TREINADOR:        'coach',
    PREPARADOR_FISICO: 'prep',
    GESTOR:           'manager',
    NUTRICIONISTA:    'athlete',   // nutrition roles see athlete view
    BIOMECANICO:      'athlete',
  };
  const defaultTabForRole = (activeRole && roleToTabKey[activeRole]) || 'calendar';

  // Only show tabs the user's role is entitled to (always include 'calendar')
  const allowedTabKeys = useMemo(() => {
    const base = ['calendar'];
    if (activeRole === 'ATLETA')            return [...base, 'athlete', 'period'];
    if (activeRole === 'TREINADOR')         return [...base, 'coach', 'period'];
    if (activeRole === 'PREPARADOR_FISICO') return [...base, 'prep', 'period'];
    if (activeRole === 'GESTOR')            return [...base, 'athlete', 'coach', 'prep', 'manager', 'period'];
    // Fallback: all tabs
    return ['calendar', 'athlete', 'coach', 'prep', 'manager', 'period'];
  }, [activeRole]);

  const ROLE_TABS_ICONS = [Calendar, Heart, ClipboardList, Gauge, Building2, BarChart3];
  const ALL_ROLE_TABS = t.roleTabs.map((tab, i) => ({ ...tab, icon: ROLE_TABS_ICONS[i] }));
  const ROLE_TABS = ALL_ROLE_TABS.filter(tab => allowedTabKeys.includes(tab.key));

  // ── State ──────────────────────────────────────────────
  const [activeRole_tab, setActiveRole_tab] = useState(() => defaultTabForRole);
  // alias kept for readability
  const activeRole_state = activeRole_tab;
  const setActiveRole_state = setActiveRole_tab;
  const [view, setView] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loadData, setLoadData] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activeFilter, setActiveFilter] = useState(filters.type || 'all');
  const [loading, setLoading] = useState(true);

  const userId = user?.id || null;

  // ── Date helpers ───────────────────────────────────────
  const getStartDate = () => {
    const d = new Date(currentDate);
    if (view === 'week') d.setDate(d.getDate() - d.getDay());
    else if (view === 'month') d.setDate(1);
    return d.toISOString().split('T')[0];
  };

  const getEndDate = () => {
    const d = new Date(currentDate);
    if (view === 'day') d.setDate(d.getDate() + 1);
    else if (view === 'week') d.setDate(d.getDate() - d.getDay() + 7);
    else { d.setMonth(d.getMonth() + 1); d.setDate(0); }
    return d.toISOString().split('T')[0];
  };

  const handlePrev = () => {
    const d = new Date(currentDate);
    if (view === 'day') d.setDate(d.getDate() - 1);
    else if (view === 'week') d.setDate(d.getDate() - 7);
    else d.setMonth(d.getMonth() - 1);
    setCurrentDate(d);
  };

  const handleNext = () => {
    const d = new Date(currentDate);
    if (view === 'day') d.setDate(d.getDate() + 1);
    else if (view === 'week') d.setDate(d.getDate() + 7);
    else d.setMonth(d.getMonth() + 1);
    setCurrentDate(d);
  };

  // ── Data fetching ──────────────────────────────────────
  useEffect(() => {
    if (activeRole_tab !== 'calendar') return;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const opts = { from: getStartDate(), to: getEndDate(), entityId: userId };
        if (activeFilter === 'competition') opts.types = 'competition,match';
        else if (activeFilter === 'training') opts.types = 'training_plan,training_session';
        if (filters.championship_id) opts.championshipId = filters.championship_id;
        if (filters.plan_id) opts.planId = filters.plan_id;

        const [eventsData, loadRes, alertsRes] = await Promise.all([
          getAgendaEvents(opts),
          AZURE.get(`/agenda/load/${userId}`, { params: { days: 30 } }).catch(() => ({ data: { data: [] } })),
          AZURE.get(`/agenda/alerts/${userId}`).catch(() => ({ data: { data: [] } })),
        ]);

        setEvents(eventsData?.events || []);
        setLoadData(loadRes.data?.data || []);
        setAlerts(alertsRes.data?.data || []);
      } catch (err) {
        console.error('[Agenda] Error:', err);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [currentDate, view, activeFilter, activeRole_tab]);

  const handleCompleteEvent = async (event) => {
    const actualTSS = prompt(`TSS Real para "${event.title}" (planejado: ${event.planned_load_tss}):`, event.planned_load_tss);
    if (!actualTSS) return;
    const rating = prompt('Como foi o treino? (1-5):', '4');
    const feedback = prompt('Feedback (opcional):', '');
    try {
      await AZURE.put(`/agenda/events/${event.event_id}/complete`, {
        user_id: userId,
        actual_load_tss: parseFloat(actualTSS),
        athlete_rating: parseInt(rating),
        athlete_feedback: feedback,
      });
      // Refresh
      setCurrentDate(new Date(currentDate));
    } catch (err) {
      console.error('[Agenda] Complete error:', err);
    }
  };

  // ── Render ─────────────────────────────────────────────
  return (
    <AgendaLangContext.Provider value={{ lang, t }}>
    <PageLayout
      header={{
        title: t.pageTitle,
        subtitle: t.pageSubtitle,
      }}
    >
      <div className="space-y-6">
        {/* ── Role Tab Bar ────────────────────────────────── */}
        <div
          className="rounded-2xl border overflow-hidden"
          style={{ borderColor: VUKZ.border, backgroundColor: '#fff' }}
        >
          <div className="flex overflow-x-auto">
            {ROLE_TABS.map((tab) => {
              const isActive = activeRole_tab === tab.key;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveRole_tab(tab.key)}
                  className="flex-1 min-w-[120px] flex flex-col items-center gap-1 py-4 px-3 transition-all border-b-2"
                  style={{
                    borderBottomColor: isActive ? VUKZ.primary : 'transparent',
                    backgroundColor: isActive ? VUKZ.primaryLight : 'transparent',
                  }}
                >
                  <Icon
                    className="w-5 h-5"
                    style={{ color: isActive ? VUKZ.primary : VUKZ.textLight }}
                  />
                  <span
                    className="text-xs font-bold"
                    style={{ color: isActive ? VUKZ.primary : VUKZ.textLight }}
                  >
                    {tab.label}
                  </span>
                  <span
                    className="text-[9px] hidden sm:block"
                    style={{ color: VUKZ.textLight }}
                  >
                    {tab.desc}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Tab Content ─────────────────────────────────── */}
        {activeRole_tab === 'calendar' && (
          <CalendarTab
            events={events}
            loading={loading}
            loadData={loadData}
            alerts={alerts}
            view={view}
            currentDate={currentDate}
            onPrev={handlePrev}
            onNext={handleNext}
            onToday={() => setCurrentDate(new Date())}
            onViewChange={setView}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            onComplete={handleCompleteEvent}
          />
        )}
        {activeRole_tab === 'athlete' && <AthleteTab />}
        {activeRole_tab === 'coach' && <CoachTab />}
        {activeRole_tab === 'prep' && <PrepTab />}
        {activeRole_tab === 'manager' && <ManagerTabImported />}
        {activeRole_tab === 'period' && <PeriodizationTab />}
      </div>
    </PageLayout>
    </AgendaLangContext.Provider>
  );
}
